import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Search, Filter, MoreVertical, ShieldCheck, Mail, Phone, Eye, Trash2, Edit, Clock, Image as ImageIcon, User } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import Drawer from '../../components/common/Drawer/Drawer';
import api from '../../api/axios';
import { confirmAction } from '../../utils/confirmToast';
import styles from './Committee.module.css';

const Committee = () => {
  const { searchQuery } = useOutletContext() || { searchQuery: '' };
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [committee, setCommittee] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMember, setEditingMember] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    role: '',
    image: '',
    isHead: false,
  });

  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchCommittee();
  }, []);

  const fetchCommittee = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/admin/committee');
      setCommittee(data);
    } catch (error) {
      toast.error('Failed to fetch committee members');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      return toast.error('Image must be less than 2MB');
    }

    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      setUploading(true);
      const { data } = await api.post('/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, image: data.url }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        await api.put(`/admin/committee/${editingMember._id}`, formData);
        toast.success('Committee member updated successfully');
      } else {
        await api.post('/admin/committee', formData);
        toast.success('Committee member added successfully');
      }
      
      setIsDrawerOpen(false);
      fetchCommittee();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save committee member');
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      role: member.role,
      image: member.image || '',
      isHead: member.isHead || false,
    });
    setIsDrawerOpen(true);
  };

  const handleDelete = (id) => {
    confirmAction(
      'Remove Member',
      'Are you sure you want to remove this committee member?',
      async () => {
        try {
          await api.delete(`/admin/committee/${id}`);
          toast.success('Committee member removed successfully');
          fetchCommittee();
        } catch (error) {
          toast.error('Failed to remove member');
        }
      }
    );
  };

  const resetForm = () => {
    setFormData({
      name: '',
      role: '',
      image: '',
      isHead: false,
    });
    setEditingMember(null);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    resetForm();
  };

  return (
    <div className={styles.container}>
      <PageHeader 
        title="Committee Leader" 
        subtitle="Manage society committee members and roles"
        actionButton={
          <button className={styles.addBtn} onClick={() => setIsDrawerOpen(true)}>
            <Plus size={16} /> Add Member
          </button>
        }
      />

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading committee members...</div>
        ) : committee.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No committee members found.</p>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Is Head?</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {committee
                .filter(member => 
                  member.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                  member.role.toLowerCase().includes(searchQuery.toLowerCase())
                )
                .map((member) => (
                <tr key={member._id}>
                  <td>
                    <div className={styles.memberInfo}>
                      <div className={styles.avatar}>
                        {member.image ? (
                          <img src={member.image} alt={member.name} />
                        ) : (
                          <div className={styles.avatarPlaceholder}>
                            <User size={20} color="#ffffff" />
                          </div>
                        )}
                      </div>
                      <div>
                        <p className={styles.memberName}>{member.name}</p>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={styles.roleBadge}>{member.role}</span>
                  </td>
                  <td>
                    <span className={member.isHead ? styles.statusActive : styles.statusInactive}>
                      {member.isHead ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    {new Date(member.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button 
                        className={styles.actionBtn} 
                        onClick={() => handleEdit(member)}
                        title="Edit Member"
                      >
                        <Edit size={16} />
                      </button>
                      <button 
                        className={`${styles.actionBtn} ${styles.deleteBtn}`}
                        onClick={() => handleDelete(member._id)}
                        title="Remove Member"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Mobile Cards (Visible only on small screens) */}
        {!loading && committee.length > 0 && (
          <div className={styles.mobileList}>
            {committee
              .filter(member => 
                member.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                member.role.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map((member) => (
              <div key={member._id} className={styles.mobileCard}>
                <div className={styles.cardHeaderArea}>
                  <div className={styles.memberInfo}>
                    <div className={styles.avatar}>
                      {member.image ? (
                        <img src={member.image} alt={member.name} />
                      ) : (
                        <div className={styles.avatarPlaceholder}>
                          <User size={20} color="#ffffff" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className={styles.memberName}>{member.name}</p>
                      <p className={styles.memberAdded}>Added {new Date(member.createdAt).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
                
                <div className={styles.cardMidArea}>
                  <span className={styles.roleBadge}>{member.role}</span>
                  <span className={member.isHead ? styles.statusActive : styles.statusInactive}>
                    {member.isHead ? 'Head' : 'Member'}
                  </span>
                </div>
                
                <div className={styles.cardBottomArea}>
                  <div className={styles.actions}>
                    <button 
                      className={styles.actionBtn} 
                      onClick={() => handleEdit(member)}
                      title="Edit Member"
                    >
                      <Edit size={16} />
                    </button>
                    <button 
                      className={`${styles.actionBtn} ${styles.deleteBtn}`}
                      onClick={() => handleDelete(member._id)}
                      title="Remove Member"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        title={editingMember ? "Edit Committee Member" : "Add Committee Member"}
      >
        <form onSubmit={handleSubmit} className={styles.form}>
          
          <div className={styles.imageUploadSection}>
            <label className={styles.formLabel}>Profile Image</label>
            <div className={styles.imagePreviewContainer}>
              {formData.image ? (
                <div className={styles.previewWrapper}>
                  <img src={formData.image} alt="Preview" className={styles.imagePreview} />
                  <button type="button" className={styles.removeImageBtn} onClick={() => setFormData({...formData, image: ''})}>×</button>
                </div>
              ) : (
                <label className={styles.uploadBox}>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageUpload}
                    className={styles.fileInput}
                    disabled={uploading}
                  />
                  <ImageIcon size={24} className={styles.uploadIcon} />
                  <span>{uploading ? 'Uploading...' : 'Click to upload image'}</span>
                  <small>JPG, PNG up to 2MB</small>
                </label>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Full Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={styles.formInput}
              placeholder="Enter member's name"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Role / Position</label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              className={styles.formInput}
              placeholder="e.g. President, Secretary, Treasurer"
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                name="isHead"
                checked={formData.isHead}
                onChange={handleInputChange}
                className={styles.checkbox}
              />
              Is Head of Committee?
            </label>
            <small className={styles.helpText}>Heads may be displayed more prominently.</small>
          </div>

          <div className={styles.formActions}>
            <button type="button" className={styles.cancelBtn} onClick={closeDrawer}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={uploading}>
              {editingMember ? 'Update Member' : 'Add Member'}
            </button>
          </div>
        </form>
      </Drawer>
    </div>
  );
};

export default Committee;
